import {
  ActionSchema,
  DynamicBooleanSchema,
  DynamicStringSchema,
  DynamicValueSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import {
  AskQuestions,
  type Clarification,
  type ClarifySubmitError,
  type ClarifySubmitResult,
} from './AskQuestions';

interface ResolvedProps {
  readonly clarifications?: Clarification[];
  readonly title?: string;
  readonly footnote?: string;
  // Populated reactively by A2UISurfaceList's result-injection branch at
  // `/results/<this component's id>`, never sent by the agent up front. A
  // rejected write arrives here too (AskQuestions opts into structured
  // error pass-through), which is how the per-field reasons reach the card.
  readonly result?: ClarifySubmitResult | ClarifySubmitError;
  readonly pending?: boolean;
  // Written by A2UISurfaceList once this surface is no longer the latest turn,
  // at `/locked`, so a stale card freezes instead of accepting a late answer.
  readonly locked?: boolean;
  readonly onSubmit?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  // A literal array of objects, fixed at emit time — nothing the user does on
  // the card changes it, so no dynamic binding is needed (same reasoning as
  // EditQuote's `changes`).
  clarifications: DynamicValueSchema,
  title: DynamicStringSchema,
  footnote: DynamicStringSchema,
  result: DynamicValueSchema,
  pending: DynamicBooleanSchema,
  locked: DynamicBooleanSchema,
  onSubmit: ActionSchema,
}) as any;

/** Whether the user submitted this card. A2UISurfaceList writes `/submitted`;
 * there's no agent binding for it, so read it off the data model directly. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readSubmitted(context: any): boolean {
  return context?.dataContext?.dataModel?.get?.('/submitted') === true;
}

/** Keep only entries the card can actually render. An agent emitting a
 * half-shaped question (no field, or no options to pick from) would otherwise
 * produce a question with no way to answer it. */
function usableClarifications(value: unknown): Clarification[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Clarification => {
    if (!item || typeof item !== 'object') return false;
    const entry = item as Record<string, unknown>;
    return (
      typeof entry.field === 'string' &&
      entry.field.length > 0 &&
      Array.isArray(entry.options) &&
      entry.options.length > 0
    );
  });
}

export const AskQuestionsImpl = createComponentImplementation(
  { name: 'AskQuestions', schema },
  // The v0_9 catalog runtime hands us `context.dataContext`, a scoped view of
  // the surface's DataModel. `dataContext.set(path, value)` pushes a pick back
  // at the given JSON pointer, which is what wires the user's answers to the
  // `/answers` slot the submit action reads. Without this the picks stay in
  // local state and Submit sends an empty map — the same failure
  // EmailComposer's editable fields would have without their write-back.
  ({
    props,
    context,
  }: {
    readonly props: ResolvedProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly buildChild: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly context: any;
  }) => (
    <AskQuestions
      clarifications={usableClarifications(props.clarifications)}
      title={props.title}
      footnote={props.footnote}
      result={props.result as ClarifySubmitResult | ClarifySubmitError | undefined}
      pending={props.pending === true}
      locked={props.locked === true}
      submitted={readSubmitted(context)}
      // A multi-select answer is written as the array itself, not a joined
      // string, so the host keeps the labels separable.
      onAnswer={(field, value) => {
        context.dataContext.set(`/answers/${field}`, value);
      }}
      onSubmit={props.onSubmit}
    />
  )
);
